import { RolodexDocumentEntry } from 'features/files'
import { DocumentDataContext } from 'features/libra/document/DocumentDataContext'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  LibraWorflowStepComponentProps,
  type UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { FC, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { z } from 'zod'

import { DocumentFieldExtractor } from './niiDataHealth/DocumentFieldExtractor'
import { NiiDataHealthTitleRow } from './niiDataHealth/NiiDataHealthTitleRow'

const CenteredMessage = styled.div<{ $color?: string }>`
  padding: 2rem;
  text-align: center;
  color: ${({ $color = 'inherit' }) => $color};
`

const Container = styled.div`
  width: 100%;
  background: transparent;
`

const ContentContainer = styled.div`
  height: 100%;
  background: transparent;
  overflow: hidden;
  padding: 1rem;
`

// No step-level config needed - required categories are now in workflow global context
export const niiDataHealthConfigSchema = z.object({})

export type NiiDataHealthConfig = z.infer<typeof niiDataHealthConfigSchema>

export const Component: FC<
  LibraWorflowStepComponentProps<NiiDataHealthConfig>
> = () => {
  const { ticketTags } = useTicketAccess()
  const unitId = ticketTags.unit as string | undefined
  const { selectedDocumentId } = useLibraDocument()

  const documentDataContext = useContext(DocumentDataContext)

  const selectedDocument = useMemo(() => {
    if (!selectedDocumentId) return null
    const documentsByCategory =
      documentDataContext?.documentsByCategory || new Map()
    for (const docs of documentsByCategory.values()) {
      const doc = docs.find(
        (d: RolodexDocumentEntry) => d.entry_id === selectedDocumentId
      )
      if (doc) return doc
    }
    return null
  }, [selectedDocumentId, documentDataContext])

  if (!unitId) {
    return (
      <CenteredMessage $color={'#999'}>
        No unit specified for this ticket. Please ensure the ticket is tagged
        with a unit.
      </CenteredMessage>
    )
  }

  return (
    <Container>
      <NiiDataHealthTitleRow unitId={unitId} />
      <ContentContainer>
        <DocumentFieldExtractor
          key={selectedDocumentId}
          selectedDocument={selectedDocument}
        />
      </ContentContainer>
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(() => {
    const result = niiDataHealthConfigSchema.safeParse(config)
    if (!result.success) {
      console.error('NiiDataHealth config validation failed:', result.error)
      console.error('Config that failed validation:', config)
    }
    return result.success
  }, [config])
}
